import { SymbolPriceTable } from "@/components/common/table/SymbolPriceTable";
import StrategyCanvasModal from "@/components/strategy_details/StrategyCanvasModal";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { API_URL, ENGINE_URL } from "@/config";
import {
  UniverseOption,
  usePatternUniverses,
} from "@/contexts/PatternUniverse";
import {
  ButtonLoaderComponent,
  formatMoney,
  getIsCrypto,
} from "@/helpers/helperFunctions";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@radix-ui/react-scroll-area";
import axios from "axios";
import { isArray, isEmpty } from "lodash";
import Link from "next/link";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BlankImg,
  PatternAPIKey,
  ScannerDescription,
  ScannerImage,
  ScannerTitle,
  ScannerValue,
  SubScannerImages,
  SubScanners,
  SubScannersLabel,
  SubScannersValue,
} from "..";
import { FAQs } from "../FAQs/faqs";
import Footer from "./Footer";
import { enableVideoTutorial } from "@/config/logos";
import { baseApi } from "@/api/baseApi";
import { useUserContext } from "@/contexts/userContext";

interface PageComponentProps {
  userLoggedIn: boolean;
  scanner: ScannerValue;
}

export type SubScannerKeygen =
  | Record<string, string>
  | Record<string, string[]>;

export type SymbolPrice = {
  price: number;
  change: number | null;
  isCrypto: boolean;
};

const PageContent: React.FC<PageComponentProps> = ({
  userLoggedIn,
  scanner,
}) => {
  const router = useRouter();
  const MainImage = useMemo(
    () => ScannerImage[scanner || ScannerValue.CHART_PATTERN],
    [scanner],
  );
  const subScannersImages = useMemo(() => SubScannerImages[scanner], [scanner]);
  const subScanners = useMemo(() => SubScanners[scanner], [scanner]);
  const { user } = useUserContext();
  const title = useMemo(() => ScannerTitle[scanner] ?? "", [scanner]);
  const Description = useMemo(
    () => ScannerDescription[scanner] ?? <></>,
    [scanner],
  );
  const { universes, refetchUniverse } = usePatternUniverses();
  const [symbolPrices, setSymbolPrices] = useState<Record<string, SymbolPrice>>(
    {},
  );
  const slug = useMemo(
    () => (router.query.slug?.length ? (router.query.slug as string[]) : []),
    [router.query],
  );

  const [selectedUniverse, setUniverse] = useState<UniverseOption>();
  const [subScannerKeygen, setSubScannerKeygen] = useState<SubScannerKeygen>(
    {},
  );

  const [selectedPat, selectPattern] = useState<string>();
  useEffect(() => {
    refetchUniverse();
  }, [refetchUniverse]);

  const [scanId, setScanId] = useState<string[]>([]);
  const [scanResult, setScanResult] = useState<Record<string, boolean>[]>([]);
  const [isScanLoading, setIsScanLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const onSubScannerClick = useCallback(
    (pattern: SubScannersValue, label: SubScannersLabel) => {
      if (pattern) {
        const { slug, ...query } = router.query;
        router.push(
          {
            query,
            pathname: `/scanner/${scanner}/${pattern}`,
          },
          void 0,
        );
      }
    },
    [router],
  );

  const runScanHandler = useCallback(async () => {
    const url = new URL("run_scan", ENGINE_URL);
    url.searchParams.append("universe_key", selectedUniverse?.value as string);
    const strategyKey = subScannerKeygen?.[selectedPat as string];
    setIsScanLoading(true);
    // In case of harmonic strategy key will be an array
    if (isArray(strategyKey)) {
      const promises = strategyKey.map((strategyKeygen) => {
        const newUrl = new URL(url);
        newUrl.searchParams.append("strategy_key", strategyKeygen);

        return baseApi.post(newUrl.toString());
      });

      try {
        const results = await Promise.all(promises);

        results.forEach((response, index) => {
          setScanId((prev) => [...prev, response.data]);
        });
      } catch (error) {}
    } else {
      url.searchParams.append("strategy_key", strategyKey);
      const res = await baseApi.post(url.toString());
      if (res.status == 200) {
        setScanId([res.data]);
      }
    }
  }, [subScannerKeygen, selectedPat, scanner, selectedUniverse]);

  useEffect(() => {
    if (slug.length) {
      if (subScanners.some((val) => val.value === slug[0]))
        selectPattern(slug[0]);
      else selectPattern("");
    } else {
      selectPattern("");
    }
  }, [slug]);

  useEffect(() => {
    let retryCount = 0;

    const fetchScanStatus = async () => {
      try {
        if (scanId.length === 0) return;

        setIsScanLoading(true);

        const promises = scanId.map((id) => {
          const url = new URL("get_run_scan_status", ENGINE_URL);
          url.searchParams.append("scan_id", id);
          return baseApi.get(url.toString());
        });

        const results = await Promise.all(promises);
        const scanData: Record<string, boolean>[] = [];

        results.forEach((response) => {
          scanData.push(response.data);
        });
        setScanId([]);
        setScanResult(scanData);
        setIsScanLoading(false);
      } catch (error) {
        console.error("Error in getRunScanStatus:", error);
        retryCount++;

        if (retryCount < 3) {
          fetchScanStatus();
        } else {
          setIsScanLoading(false);
        }
      }
    };

    fetchScanStatus();

    return () => {
      retryCount = 0;
    };
  }, [scanId]);

  useEffect(() => {
    if (user) return;
    const timer = setTimeout(() => {
      setIsModalOpen(true);
    }, 55000);

    return () => clearTimeout(timer);
  }, [user]);

  useEffect(() => {
    getPreBuiltStrategies(PatternAPIKey[scanner] ?? "");
  }, [scanner]);

  useEffect(() => {
    if (!selectedUniverse) return;
    const symbols = selectedUniverse?.uList?.join(",") as string;
    const url = new URL("/get_tickers_snapshot", API_URL);
    url.searchParams.append("tickers", symbols);

    axios
      .get(url.href, {})
      .then((response) => {
        if (response.status === 200) {
          const data = response?.data || {};
          const processedData: Record<string, SymbolPrice> = {};

          Object.entries(data).forEach(([symbol, details]: [string, any]) => {
            if (getIsCrypto(symbol)) {
              const price = formatMoney(details.lastQuote.a, 5, "--");
              const change = null;
              processedData[symbol] = {
                price,
                change,
                isCrypto: getIsCrypto(symbol),
              };
            } else {
              const price = formatMoney(details.todaysChange, 2, "--");
              const change = Number(details.todaysChangePerc.toFixed(2) || "-");
              processedData[symbol] = {
                price,
                change,
                isCrypto: getIsCrypto(symbol),
              };
            }
          });
          setSymbolPrices(processedData);
        }
      })
      .catch((error) => {
        console.error("Error fetching prices:", error);
      });
  }, [selectedUniverse]);

  const getPreBuiltStrategies = useCallback(
    async (selectedScanner: string) => {
      try {
        const url = new URL("get_market_scanner_pre_built_strategies", API_URL);
        url.searchParams.append("type", selectedScanner);
        const res = await baseApi.post(url.toString());
        if (res.status === 200) {
          const data: SubScannerKeygen = res?.data;
          setSubScannerKeygen(data);
        }
      } catch (e) {
        console.error(e);
      }
    },
    [scanner, user],
  );

  const removeLastWord = useCallback((str: string) => {
    return str.trim().split(" ").slice(0, -1).join(" ");
  }, []);

  const removeLastLetter = useCallback((str: string) => {
    return str.trim().slice(0, -1);
  }, []);

  useEffect(() => {
    setScanId([]);
    setScanResult([]);
  }, [selectedUniverse, selectedPat, scanner]);

  return (
    <div className="w-full max-w-[984px] md:mx-auto lg:px-0 px-5">
      <div className={`flex flex-col items-center`}>
        <div className="w-full flex flex-col sm:gap-8 gap-4">
          <div className="flex gap-4 flex-col">
            <Label className="text-2xl font-bold">{title}</Label>
            <div className="flex md:flex-row flex-col md:gap-6 gap-2">
              <div className="md:w-2/5 w-full">
                <Card className=" border-blue-700 bg-transparent">
                  <div className="lg:max-w-full max-w-96 w-full mx-auto ">
                    <AspectRatio ratio={1.59 /* 812/515 */}>
                      <MainImage
                        className="object-contain"
                        width="100%"
                        height="100%"
                      />
                    </AspectRatio>
                  </div>
                </Card>
              </div>

              <div className="md:w-3/5 w-full relative text-sm flex flex-col gap-1 justify-start">
                <Description router={router} />
                {enableVideoTutorial && (
                  <div className=" w-[59%] rounded-xl overflow-hidden aspect-video">
                    <iframe
                      width="100%"
                      height="100%"
                      src="https://www.youtube.com/embed/Ghc9KOvGqX0"
                      title="YouTube video player"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-4 flex-col">
            <Label className="text-xl font-semibold">
              {" "}
              <span className="text-[--accent-primary]">Step one: </span>Select
              a Scanner
            </Label>
            <div className="grid w-full gap-2 md:gap-4 sm:grid-cols-2 md:grid-cols-4">
              {subScanners.map((subScanner, i) => {
                const Img = subScannersImages?.[subScanner.value] ?? BlankImg;
                const isSelected = subScanner.value === selectedPat;
                return (
                  <Card
                    key={i}
                    onClick={() =>
                      onSubScannerClick(subScanner.value, subScanner.label)
                    }
                    className={cn(
                      "w-full pt-4",
                      isSelected
                        ? "bg-black border-blue-700"
                        : "bg-neutral-800 cursor-pointer hover:bg-card",
                    )}
                  >
                    <CardContent className="pb-0">
                      <AspectRatio ratio={1.333}>
                        <Img
                          className="object-contain"
                          width="100%"
                          height="100%"
                        />
                      </AspectRatio>
                    </CardContent>
                    <CardFooter className="justify-center">
                      <CardTitle className="text-md font-bold text-center mt-2">
                        {subScanner.label}
                      </CardTitle>
                    </CardFooter>
                  </Card>
                );
              })}
            </div>
          </div>
          <div className="text-center text-muted-foreground text-xs">
            Looking to create a custom scanner?{" "}
            <Link
              href={userLoggedIn ? "/test?tab=scanners" : "/"}
              className={buttonVariants({
                variant: "link",
                className: "pl-0 !text-blue-600 text-xs",
              })}
            >
              {userLoggedIn ? "Click here" : "Sign in now"}
            </Link>
          </div>
          <div className="flex gap-10">
            <div className="flex-1 flex flex-col gap-2">
              <Label className="text-xl font-semibold">
                <span className="text-[--accent-primary]">Step two: </span>
                Select instruments to scan
              </Label>
              <div className="flex flex-row gap-2 items-center justify-between">
                <Select
                  onValueChange={(select?: string) => {
                    const _value = universes.find(
                      (inst) => inst.value === select,
                    );
                    _value && setUniverse(_value);
                  }}
                  name="universe"
                  value={selectedUniverse?.value}
                  required
                >
                  <SelectTrigger className="max-w-80">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <ScrollArea className="h-48 max-h-56">
                        {universes.map((option, index) => (
                          <SelectItem
                            value={option.value}
                            key={`u-option-${index}`}
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </ScrollArea>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex-1 flex flex-col gap-2 items-end">
              <Label className="text-xl text-end font-semibold">
                <span className="text-[--accent-primary]">Step three: </span>Run{" "}
                {removeLastWord(title)} scan
              </Label>
              {isScanLoading ? (
                <div className="mr-10 w-auto p-2">
                  <ButtonLoaderComponent />
                </div>
              ) : (
                <Button
                  onClick={runScanHandler}
                  disabled={selectedPat == "" || isEmpty(selectedUniverse)}
                  className="w-auto"
                >
                  Run Scan
                </Button>
              )}
            </div>
          </div>

          <div>
            <Separator />
            <div className="flex gap-4 min-h-40">
              <div className="min-w-72">
                <SymbolPriceTable
                  selectedUniverse={selectedUniverse}
                  symbolPrices={symbolPrices}
                />
              </div>
              <div className="w-px my-6 border-l border-input"> </div>
              <div className="grow">
                <Table className="w-full">
                  <TableHeader>
                    {(() => {
                      const selectedScanner = subScanners.find(
                        (scanner) => scanner.value === selectedPat,
                      );

                      return scanner === ScannerValue.HARMONIC_PATTERN ? (
                        <TableRow className="text-center text-foreground">
                          {selectedScanner ? (
                            <>
                              <TableHead>
                                Bearish {selectedScanner.label}
                              </TableHead>
                              <TableHead>
                                Bullish {selectedScanner.label}
                              </TableHead>
                            </>
                          ) : (
                            <TableHead>Select a scanner</TableHead>
                          )}
                        </TableRow>
                      ) : (
                        <TableRow className="text-center text-foreground">
                          {selectedScanner ? (
                            <TableHead>{selectedScanner.label}</TableHead>
                          ) : (
                            <TableHead>Select a scanner</TableHead>
                          )}
                        </TableRow>
                      );
                    })()}
                  </TableHeader>

                  <TableBody className="text-muted-foreground">
                    {selectedUniverse?.uList?.map((universe) => (
                      <TableRow key={universe}>
                        {isEmpty(scanResult) ? (
                          scanner === ScannerValue.HARMONIC_PATTERN ? (
                            <>
                              <TableCell className="text-white">--</TableCell>
                              <TableCell className="text-white">--</TableCell>
                            </>
                          ) : (
                            <TableCell className="text-white">--</TableCell>
                          )
                        ) : (
                          scanResult.map((res, index) => (
                            <TableCell key={index} className="text-white">
                              {res[universe] ? (
                                <p className="text-green-foreground">
                                  Pattern detected
                                </p>
                              ) : (
                                <p className="text-red-foreground">
                                  No pattern detected
                                </p>
                              )}
                            </TableCell>
                          ))
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            <div className="my-10">
              <div className="flex text-center justify-center">
                <p className="text-xs w-[500px] text-muted-foreground">
                  Now that you've seen how effectively our strategy scanners
                  identify technical chart patterns, sign up to create your own
                  scanner and deploy live—experience data-driven analysis and
                  intelligent automation with Level2.
                </p>
              </div>
            </div>
            {!userLoggedIn && (
              <div className="text-center text-xs">
                <Label className="text-xl font-semibold">
                  <span className="text-[--accent-primary]">Step four: </span>
                  <a href="/create-new-account">
                    <u>Sign up</u>
                  </a>{" "}
                  to deploy
                </Label>
              </div>
            )}
          </div>
          {isModalOpen && (
            <StrategyCanvasModal onClose={function (): void {}} />
          )}

          <FAQs type={scanner} />
          <div className="mt-10">
            <Footer userLoggedIn={userLoggedIn} scanner={scanner} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageContent;
